/**
 * Money denomination style definitions and display helpers.
 *
 * Extracted from MoneyAssembler.tsx during CL-1 s65.
 * Pure data + pure functions — no React dependency.
 */

import type { MoneyDenom } from '../../content/types'

// ── Style Types ──────────────────────────────────────────────

export interface DenomStyle {
  container: string
  bg: string
  text: string
  border: string
  inner: string
}

// ── Bill Styles ──────────────────────────────────────────────

const BILL_STYLES: Record<number, DenomStyle> = {
  5000: {
    container: 'w-28 h-16 md:w-32 md:h-18 rounded-xl shadow-xl',
    bg: 'bg-orange-500 border-orange-700',
    text: 'text-white font-black text-2xl md:text-3xl drop-shadow-lg',
    border: 'border-4 border-opacity-40',
    inner: 'bg-white/15 backdrop-blur-sm',
  },
  2000: {
    container: 'w-26 h-15 md:w-30 md:h-17 rounded-xl shadow-xl',
    bg: 'bg-blue-500 border-blue-700',
    text: 'text-white font-black text-2xl md:text-3xl drop-shadow-lg',
    border: 'border-4 border-opacity-40',
    inner: 'bg-white/15 backdrop-blur-sm',
  },
  1000: {
    container: 'w-24 h-14 md:w-28 md:h-16 rounded-xl shadow-xl',
    bg: 'bg-rose-500 border-rose-700',
    text: 'text-white font-black text-xl md:text-2xl drop-shadow-lg',
    border: 'border-4 border-opacity-40',
    inner: 'bg-white/15 backdrop-blur-sm',
  },
  500: {
    container: 'w-22 h-13 md:w-26 md:h-15 rounded-xl shadow-lg',
    bg: 'bg-slate-500 border-slate-700',
    text: 'text-white font-black text-xl md:text-2xl drop-shadow-lg',
    border: 'border-3 border-opacity-40',
    inner: 'bg-white/15 backdrop-blur-sm',
  },
}

// ── Coin Styles ──────────────────────────────────────────────

const COIN_STYLES: Record<number, DenomStyle> = {
  200: {
    container: 'w-16 h-16 md:w-18 md:h-18 rounded-full shadow-2xl',
    bg: 'bg-slate-200 border-slate-500',
    text: 'text-slate-800 font-black text-lg md:text-xl',
    border: 'border-[3px]',
    inner:
      'bg-amber-400 rounded-full w-10 h-10 md:w-12 md:h-12 flex items-center justify-center shadow-inner border-2 border-amber-600/40',
  },
  100: {
    container: 'w-14 h-14 md:w-16 md:h-16 rounded-full shadow-2xl',
    bg: 'bg-amber-400 border-amber-600',
    text: 'text-amber-900 font-black text-lg md:text-xl',
    border: 'border-[3px]',
    inner:
      'bg-slate-200 rounded-full w-9 h-9 md:w-11 md:h-11 flex items-center justify-center shadow-inner border-2 border-slate-500/40',
  },
  50: {
    container: 'w-14 h-14 md:w-16 md:h-16 rounded-full shadow-xl',
    bg: 'bg-yellow-400 border-yellow-600',
    text: 'text-yellow-900 font-black text-base md:text-lg',
    border: 'border-[3px]',
    inner: 'w-full h-full flex items-center justify-center',
  },
  20: {
    container: 'w-12 h-12 md:w-14 md:h-14 rounded-full shadow-lg',
    bg: 'bg-yellow-400 border-yellow-600',
    text: 'text-yellow-900 font-black text-base md:text-lg',
    border: 'border-[3px]',
    inner: 'w-full h-full flex items-center justify-center',
  },
  10: {
    container: 'w-11 h-11 md:w-13 md:h-13 rounded-full shadow-lg',
    bg: 'bg-yellow-400 border-yellow-700',
    text: 'text-yellow-900 font-black text-sm md:text-base',
    border: 'border-[3px]',
    inner: 'w-full h-full flex items-center justify-center',
  },
  5: {
    container: 'w-10 h-10 md:w-12 md:h-12 rounded-full shadow-md',
    bg: 'bg-orange-400 border-orange-700',
    text: 'text-orange-900 font-black text-sm md:text-base',
    border: 'border-[3px]',
    inner: 'w-full h-full flex items-center justify-center',
  },
  2: {
    container: 'w-9 h-9 md:w-11 md:h-11 rounded-full shadow-md',
    bg: 'bg-orange-400 border-orange-700',
    text: 'text-orange-900 font-black text-xs md:text-sm',
    border: 'border-[2.5px]',
    inner: 'w-full h-full flex items-center justify-center',
  },
  1: {
    container: 'w-8 h-8 md:w-10 md:h-10 rounded-full shadow-md',
    bg: 'bg-orange-400 border-orange-700',
    text: 'text-orange-900 font-black text-xs md:text-sm',
    border: 'border-[2.5px]',
    inner: 'w-full h-full flex items-center justify-center',
  },
}

const DEFAULT_STYLE: DenomStyle = {
  container: 'w-12 h-12 rounded-full',
  bg: 'bg-gray-200',
  text: 'text-gray-800',
  border: 'border',
  inner: '',
}

// ── Public Helpers ────────────────────────────────────────────

/** Get visual style for a denomination value in cents. */
export function getMoneyStyles(cents: number): DenomStyle {
  return BILL_STYLES[cents] ?? COIN_STYLES[cents] ?? DEFAULT_STYLE
}

/** Check whether a denomination is a bill (not a coin). */
export function isBill(cents: number, allowedDenoms: MoneyDenom[]): boolean {
  const denom = allowedDenoms.find((d) => d.valueCents === cents)
  return denom?.type === 'bill'
}

/** Get display label for a denomination (uses config label or € formatting). */
export function getDenominationDisplay(
  cents: number,
  allowedDenoms: MoneyDenom[],
): string {
  const denom = allowedDenoms.find((d) => d.valueCents === cents)
  if (denom?.label) return denom.label
  if (cents >= 100) return `€${cents / 100}`
  return `${cents}`
}
